import { Loadable } from './util';

const AVAILABILITY_SERVICE_PATH = '/portal-services/oci/availability/{{productid}}/PortalShop';

/**
 * List of possible availability statuses
 */
export const enum AvailabilityStatus {
	OnStock = 'OnStock',
	OutOfStock = 'OutOfStock',
	OutOfStockReservable = 'OutOfStockReservable',
	OutOfStockNotifiable = 'OutOfStockNotifiable',
	ShortnessOfSupplies = 'ShortnessOfSupplies',
	DelayedDelivery = 'DelayedDelivery',
	Undefined = 'Undefined',
}

interface AvailabilityData {
	state? : AvailabilityStatus;
}

export class Availability extends Loadable<AvailabilityData, any, AvailabilityStatus> {
	public availability : AvailabilityStatus;

	private productId : string;

	constructor(productId : string) {
		super();
		this.availability = AvailabilityStatus.Undefined;
		this.productId = productId;
	}


	/**
	 * Returns String for availability status
	 * See {@link AvailabilityStatus} for possible values
	 * @returns {string}
	 */
	public getStatus() {
		return this.availability || AvailabilityStatus.Undefined;
	}

	/**
	 * True if a product is currently on stock
	 */
	public isAvailable() {
		return this.getStatus() === AvailabilityStatus.OnStock ||
			this.getStatus() === AvailabilityStatus.ShortnessOfSupplies ||
			this.getStatus() === AvailabilityStatus.DelayedDelivery;
	}

	/**
	 * True if an ecommerce order can be placed
	 * This holds even if out of stock but reservable.
	 * Not however, if it is notifyable because that’s not an order process
	 */
	public isOrderable() {
		return this.isAvailable() || this.getStatus() === AvailabilityStatus.OutOfStockReservable;
	}

	/**
	 * True if people can opt-in to be notified about when a product becomes available.
	 * out-of-stock products have this if they’re not being phased out.
	 */
	public isNotifyable() {
		return this.getStatus() === AvailabilityStatus.OutOfStockNotifiable;
	}

	/**
	 * True if the item is out-of-stock but some action can still be placed on it or is on-stock.
	 */
	public isActionable() {
		return this.isOrderable() || this.isNotifyable() || this.getStatus() === AvailabilityStatus.Undefined;
	}

	/**
	 * True if the item is out-of-stock. Does also return true if the item is actionable (notifyable or preorderable).
	 */
	public isOutOfStock() {
		return this.getStatus().indexOf(AvailabilityStatus.OutOfStock) > -1;
	}

	// From Loadable
	protected loadFunction() {
		return $.ajax({
			accepts: {json: 'application/json'},
			dataType: 'json',
			url: AVAILABILITY_SERVICE_PATH.replace('{{productid}}', this.productId),
		}) as JQuery.Promise3<
			AvailabilityData, any, never,
			JQuery.Ajax.SuccessTextStatus, JQuery.Ajax.ErrorTextStatus, never,
			any, any, never
		>;
	}

	// From Loadable
	protected loadCallback(data : AvailabilityData) : AvailabilityStatus {
		if(data && 'state' && data.state) {
			this.availability = data.state;
		}

		return this.availability;
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Availability.ts